import React, { useState, useEffect } from 'react';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  Text,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  FormControl,
  FormLabel,
  FormHelperText,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
  Divider,
  Select,
} from '@chakra-ui/react';

import { FaPlay } from 'react-icons/fa';

const StartProcessModal = ({
  isOpen,
  onClose,
  onConfirm,
  currentSettings,
  storeOptions = [],
  selectedStoreId,
  onStoreChange,
  supplierOptions = [],
  selectedSupplierId,
  onSupplierChange,
  isLoading = false,
}) => {
  const [maxListings, setMaxListings] = useState(100);
  const [isValid, setIsValid] = useState(true);

  // Load current settings when modal opens
  useEffect(() => {
    if (isOpen && currentSettings?.maxListingsPerRun) {
      setMaxListings(currentSettings.maxListingsPerRun);
    }
  }, [isOpen, currentSettings]);

  // Validate max listings input and store selection
  useEffect(() => {
    setIsValid(maxListings > 0 && maxListings <= 25000 && selectedStoreId);
  }, [maxListings, selectedStoreId]);

  const handleConfirm = () => {
    if (isValid) {
      onConfirm(maxListings, selectedSupplierId);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Box display="flex" alignItems="center" gap={3}>
            <Box
              p={2}
              borderRadius="full"
              bg="green.100"
              color="green.600"
              _dark={{ bg: 'green.900', color: 'green.300' }}
            >
              <FaPlay size="16px" />
            </Box>
            <Text>Start Optimization Process</Text>
          </Box>
        </ModalHeader>

        <ModalBody>
          <VStack spacing={6} align="stretch">
            {/* Info Alert */}
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertTitle fontSize="sm">Process Information</AlertTitle>
                <AlertDescription fontSize="sm">
                  This will start the optimization process to analyze competitor pricing
                  and suggest optimal prices for your listings.
                </AlertDescription>
              </Box>
            </Alert>

            <Divider />

            {/* Store Selection */}
            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight="semibold">
                Select Store
              </FormLabel>
              <Select
                value={selectedStoreId || ''}
                onChange={(e) => onStoreChange(e.target.value)}
                placeholder="Choose a store for optimization"
                size="lg"
                _focus={{
                  borderColor: 'green.500',
                  boxShadow: '0 0 0 1px green.500',
                }}
              >
                {storeOptions.map((store) => (
                  <option key={store.id} value={store.value}>
                    {store.label}
                  </option>
                ))}
              </Select>
              <FormHelperText fontSize="xs" color="gray.600">
                Select the store you want to optimize listings for
              </FormHelperText>
            </FormControl>

            {/* Supplier Selection */}
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="semibold">
                Filter by Supplier (Optional)
              </FormLabel>
              <Select
                value={selectedSupplierId || ''}
                onChange={(e) => onSupplierChange(e.target.value)}
                placeholder="All suppliers"
                size="lg"
                _focus={{
                  borderColor: 'green.500',
                  boxShadow: '0 0 0 1px green.500',
                }}
              >
                <option value="">All suppliers</option>
                {supplierOptions.map((supplier) => (
                  <option key={supplier.id} value={supplier.value}>
                    {supplier.label}
                  </option>
                ))}
              </Select>
              <FormHelperText fontSize="xs" color="gray.600">
                Optionally filter listings by a specific supplier
              </FormHelperText>
            </FormControl>

            {/* Max Listings Input */}
            <FormControl isInvalid={!isValid}>
              <FormLabel fontSize="sm" fontWeight="semibold">
                Maximum Listings to Process
              </FormLabel>
              <NumberInput
                value={maxListings}
                onChange={(valueString, valueNumber) => setMaxListings(valueNumber)}
                min={1}
                max={25000}
                step={1000}
                size="lg"
              >
                <NumberInputField
                  placeholder="Enter max listings"
                  _focus={{
                    borderColor: 'green.500',
                    boxShadow: '0 0 0 1px green.500',
                  }}
                />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <FormHelperText fontSize="xs" color="gray.600">
                Choose how many listings to process in this run (1 - 25,000)
              </FormHelperText>
              {!selectedStoreId && (
                <FormHelperText fontSize="xs" color="red.500">
                  Please select a store before starting the process
                </FormHelperText>
              )}
            </FormControl>

            {/* Current Settings Info */}
            {currentSettings && (
              <Box
                p={4}
                bg="gray.50"
                borderRadius="md"
                borderWidth="1px"
                borderColor="gray.200"
                _dark={{ bg: 'gray.700', borderColor: 'gray.600' }}
              >
                <Text
                  fontSize="sm"
                  fontWeight="semibold"
                  mb={2}
                  color="gray.700"
                  _dark={{ color: 'gray.300' }}
                >
                  Current Settings
                </Text>
                <VStack spacing={1} align="stretch">
                  <Box display="flex" justifyContent="space-between">
                    <Text fontSize="xs" color="gray.600" _dark={{ color: 'gray.400' }}>
                      Default Max Listings:
                    </Text>
                    <Text
                      fontSize="xs"
                      fontWeight="medium"
                      color="gray.800"
                      _dark={{ color: 'gray.200' }}
                    >
                      {currentSettings.maxListingsPerRun?.toLocaleString() || '25,000'}
                    </Text>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Text fontSize="xs" color="gray.600" _dark={{ color: 'gray.400' }}>
                      Priority Competitors:
                    </Text>
                    <Text
                      fontSize="xs"
                      fontWeight="medium"
                      color="gray.800"
                      _dark={{ color: 'gray.200' }}
                    >
                      {currentSettings.priorityCompetitors?.length || 0}
                    </Text>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Text fontSize="xs" color="gray.600" _dark={{ color: 'gray.400' }}>
                      Scheduled Runs:
                    </Text>
                    <Text
                      fontSize="xs"
                      fontWeight="medium"
                      color="gray.800"
                      _dark={{ color: 'gray.200' }}
                    >
                      {currentSettings.schedule?.filter((s) => s.enabled)?.length || 0}{' '}
                      active
                    </Text>
                  </Box>
                </VStack>
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose} isDisabled={isLoading}>
            Cancel
          </Button>
          <Button
            colorScheme="green"
            onClick={handleConfirm}
            isDisabled={!isValid || isLoading}
            isLoading={isLoading}
            loadingText="Starting..."
            leftIcon={<FaPlay />}
          >
            Start Process
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default StartProcessModal;
